/**
 * The mail server's own folders, at the foot of the inbox rail.
 *
 * Everything above this in the rail is a saved query over thread status
 * (`mailbox.ts`); this is the account itself, read live over IMAP. Clicking a
 * folder opens it in the two panes to the right, and reading it changes
 * nothing - the CRM stores no mail in these folders and the poller still reads
 * INBOX alone, so what the click opens is a window onto the mail server rather
 * than a part of the inbox. Changing mail there is always a menu entry.
 *
 * A row is a real button, and that is not a detail: it shipped as a span, so
 * the pointer turned into a text caret over a row that lit up on hover and then
 * did nothing when pressed. Only a selectable folder gets one - a \Noselect
 * node is a path segment rather than a mailbox, and no mail client lets you
 * open one.
 *
 * The row actions appear on hover and on keyboard focus, and are drawn only for
 * a folder the server will actually let go of: INBOX and the special-use
 * folders carry no buttons at all, rather than buttons that explain themselves
 * after the press. They sit OUTSIDE the row button - a button inside a button
 * is invalid HTML and the browser is free to drop one of them.
 *
 * The section is mounted only when inbound polling is on, so a mailbox nobody
 * reads does not pay for an IMAP round trip on every visit to the inbox.
 *
 * A right-click (or a long press) on a folder raises the same kind of menu the
 * thread list has - see `ImapFolderItems`. It repeats the hover buttons and
 * adds opening, marking the whole folder read and making a folder inside this
 * one. The counts next to the names are corrected in place when the open
 * folder's list files a message (`folderCounts.ts`), rather than by reading the
 * whole tree again after every click.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Archive,
  ChevronRight,
  FileEdit,
  Folder,
  FolderPlus,
  Inbox,
  Loader2,
  PenLine,
  Send,
  ShieldAlert,
  Trash2,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { cn } from "@wa/ui";
import { useT } from "../../../../i18n/I18nProvider";
import { getImapFolders, type ImapFolder } from "../../../../lib/mail";
import { markImapFolderRead } from "../../../../lib/mailActions";
import { ContextMenu, useContextMenu } from "../../../shell/ContextMenu";
import { DeleteImapFolderModal } from "./DeleteImapFolderModal";
import { applyCountChanges } from "./folderCounts";
import { ImapFolderItems } from "./ImapFolderItems";
import { imapFolderError, imapReasonText } from "./imapFolderErrors";
import { announceMailbox, useMailboxEvents } from "./mailboxEvents";
import { NewImapFolderModal } from "./NewImapFolderModal";
import { RenameImapFolderModal } from "./RenameImapFolderModal";
import { isMainFolder } from "../rail/view";

/** A special-use folder gets its own icon; everything else is a folder. */
const SPECIAL_ICON: Record<string, LucideIcon> = {
  "\\Inbox": Inbox,
  "\\Sent": Send,
  "\\Drafts": FileEdit,
  "\\Junk": ShieldAlert,
  "\\Trash": Trash2,
  "\\Archive": Archive,
};

/** Nesting is drawn from the depth the API already worked out. */
const INDENT_PX = 11;

/**
 * Which folders carry no buttons. The server enforces the same rule and is the
 * authority; this is here so a folder that cannot go is never offered, rather
 * than refused after the press.
 */
function isProtected(f: ImapFolder): boolean {
  return f.specialUse !== null || f.path.toUpperCase() === "INBOX";
}

const ACT_BTN = "grid size-[22px] place-items-center rounded-full transition-colors";

export function ImapFolders({
  mailbox,
  canManage,
  openPath,
  onOpen,
  onClose,
  onToast,
  onFolders,
  pinned,
  onPin,
}: {
  /** Whose server this is: "shared" or an own mailbox id. */
  mailbox: string;
  canManage: boolean;
  /** The folder currently open in the panes, so the rail can mark it. */
  openPath: string | null;
  onOpen: (path: string) => void;
  /** Back to the CRM's own folders, for when the open one stops existing. */
  onClose: () => void;
  onToast: (msg: string) => void;
  /** Every folder the server listed, INBOX and Sent included: the mailbox's
   *  tree finds its Sent row in here. */
  onFolders?: (folders: ImapFolder[]) => void;
  /** Whether a folder is pinned under Favorites, and the toggle for it (in the menu). */
  pinned?: (path: string) => boolean;
  onPin?: (path: string) => void;
}) {
  const t = useT();
  /**
   * Shut by default. These are the account's own folders - Trash, the server's
   * Drafts and Junk, whatever somebody filed in Outlook - and drawn open under
   * the mailbox's six rows they read as a second Drafts and a second Spam,
   * which is the muddle this tree was rebuilt to end. The open folder keeps it
   * open, so a folder being read never hides its own row.
   */
  const [expanded, setExpanded] = useState(false);
  const onFoldersRef = useRef(onFolders);
  onFoldersRef.current = onFolders;
  const [folders, setFolders] = useState<ImapFolder[]>([]);
  /** The server's hierarchy separator, needed to tell a renamed ancestor from a
   *  folder whose name merely begins with the same letters. */
  const [delimiter, setDelimiter] = useState("/");
  const [reason, setReason] = useState<string | null>(null);
  const [busy, setBusy] = useState(true);
  /** The "new folder" dialog, and the parent it opens with - "" is the top level. */
  const [adding, setAdding] = useState<{ parent: string } | null>(null);
  const menu = useContextMenu<ImapFolder>();
  const [renaming, setRenaming] = useState<ImapFolder | null>(null);
  const [deleting, setDeleting] = useState<ImapFolder | null>(null);

  // A folder with something under it is renameable but not deletable - the
  // server refuses it, and the rail says so by not offering the button. See
  // `checkFolderOp` for why the subtree is not taken along.
  const parents = useMemo(
    () => new Set(folders.map((f) => f.parentPath).filter((p) => p !== "")),
    [folders],
  );

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const res = await getImapFolders(mailbox);
      setFolders(res.ok ? res.folders : []);
      onFoldersRef.current?.(res.ok ? res.folders : []);
      if (res.ok && res.delimiter) setDelimiter(res.delimiter);
      setReason(res.ok ? null : (res.reason ?? "imap_error"));
    } catch {
      setFolders([]);
      setReason("imap_error");
    } finally {
      setBusy(false);
    }
  }, [mailbox]);

  useEffect(() => {
    void load();
  }, [load]);

  // The open folder's list filed a message: move its counts, no round trip.
  // Only this mailbox's: the other trees have folders of the same name.
  useMailboxEvents((event) => {
    if (event.kind === "counts" && event.mailbox === mailbox) {
      setFolders((prev) => applyCountChanges(prev, event.changes));
    }
  });

  /** The tree draws INBOX and Sent as rows of its own; the rest live in here. */
  const shown = useMemo(() => folders.filter((f) => !isMainFolder(f)), [folders]);
  const open = expanded || (openPath !== null && shown.some((f) => f.path === openPath));

  async function markRead(f: ImapFolder) {
    try {
      const res = await markImapFolderRead(f.path, mailbox);
      setFolders((prev) => applyCountChanges(prev, [{ path: res.path, unseenTo: 0 }]));
      // The list on screen may be this folder, and every bold row in it is
      // now wrong; it reads itself again when it hears this.
      announceMailbox({ kind: "folderRead", mailbox, path: res.path });
      onToast(t("email.imap.markedRead", { name: f.name }));
    } catch (err) {
      onToast(imapFolderError(t, err, "email.imap.markReadFailed"));
    }
  }

  return (
    <>
      <div className="crm-mtag flex-none items-center gap-2 pr-2">
        <button
          type="button"
          className="flex min-w-0 flex-1 items-center gap-1.5 text-left"
          aria-expanded={open}
          onClick={() => setExpanded((v) => !v)}
        >
          <ChevronRight className={cn("size-3.5 shrink-0 transition-transform", open && "rotate-90")} />
          <span className="crm-mtag-l">{t("email.imap.more")}</span>
        </button>
        {canManage && open ? (
          <button
            type="button"
            className="crm-cb crm-cb-sm"
            onClick={() => setAdding({ parent: "" })}
            title={t("email.imap.newTitle")}
            aria-label={t("email.imap.newTitle")}
          >
            <FolderPlus className="size-3.5" />
          </button>
        ) : null}
      </div>

      {open && busy ? (
        <span className="crm-mtag">
          <Loader2 className="size-3.5 shrink-0 animate-spin" />
          <span className="crm-mtag-l">{t("email.imap.loading")}</span>
        </span>
      ) : null}

      {/* The reason travels: "IMAP not configured" and "password rejected" are
          different problems, and a rail that showed neither would send the
          operator to the settings page for a fault that is not there. */}
      {open && !busy && reason ? (
        <p className="px-[14px] pb-1 text-[11.5px] leading-snug text-muted-foreground">
          {imapReasonText(t, reason, "email.imap.listFailed")}
        </p>
      ) : null}

      {(open ? shown : []).map((f) => {
        const Icon = (f.specialUse && SPECIAL_ICON[f.specialUse]) || Folder;
        const count = f.unseen || f.messages;
        const actions = canManage && !isProtected(f);
        // Opening a folder reads the mail inside it, which the API gates on
        // `manage:crm.email` - the mailbox owner's permission, not the one every
        // operator holds. A row nobody may open is drawn as plain text rather
        // than as a button that answers 403.
        const openable = canManage && f.selectable;
        const on = f.path === openPath;
        const inner = (
          <>
            <Icon className="size-3.5 shrink-0" strokeWidth={1.8} />
            <span className="crm-mtag-l">{f.name}</span>
            {count ? (
              <span
                className={cn(
                  "crm-n ml-auto transition-opacity",
                  f.unseen && "crm-n-hot",
                  // The count steps aside for the buttons that take its place.
                  actions && "group-focus-within:opacity-0 group-hover:opacity-0",
                )}
              >
                {count}
              </span>
            ) : null}
          </>
        );
        const pad = { paddingLeft: 14 + f.depth * INDENT_PX };
        return (
          // The wrapper carries the hover group and the positioning context; the
          // row inside it carries the click. `flex-none` because the rail is a
          // flex column and `.crm-mtag` sets it on the rows themselves - without
          // it the wrapper would stretch and the row would grow with it.
          <span key={f.path} className="group relative flex-none" {...(canManage ? menu.rowProps(f) : {})}>
            {openable ? (
              <button
                type="button"
                className={cn("crm-mtag", on && "is-on")}
                style={pad}
                title={f.path}
                aria-current={on ? "true" : undefined}
                onClick={() => onOpen(f.path)}
              >
                {inner}
              </button>
            ) : (
              // A \Noselect node cannot be opened at all - it is a path segment
              // rather than a mailbox - and neither can a folder this operator
              // may not read. Either way: no button, no pointer, no hover lift,
              // rather than a row that lights up and then does nothing.
              <span className="crm-mtag cursor-default hover:bg-transparent" style={pad} title={f.path}>
                {inner}
              </span>
            )}

            {/* Out of the flow on purpose. In it, the buttons would hold their
                width while invisible and squeeze every folder name in the rail -
                an indented child ended up reading "20…" instead of "2026".
                Revealed by hover OR focus, so the keyboard keeps them - and
                shown outright where there is no hover at all, because a touch
                screen never fires one and rename and delete would simply not
                exist. The rail is a 304px sheet there, so it has the room. */}
            {actions ? (
              <span className="absolute right-2.5 top-1/2 flex -translate-y-1/2 items-center gap-0.5 opacity-0 transition-opacity focus-within:opacity-100 group-hover:opacity-100 [@media(hover:none)]:opacity-100">
                <button
                  type="button"
                  className={cn(ACT_BTN, "text-muted-foreground hover:text-foreground")}
                  onClick={() => setRenaming(f)}
                  title={t("email.imap.renameTitle")}
                  aria-label={t("email.imap.renameTitle")}
                >
                  <PenLine className="size-3" />
                </button>
                {parents.has(f.path) ? null : (
                  <button
                    type="button"
                    className={cn(ACT_BTN, "text-muted-foreground hover:text-destructive")}
                    onClick={() => setDeleting(f)}
                    title={t("email.imap.deleteTitle")}
                    aria-label={t("email.imap.deleteTitle")}
                  >
                    <Trash2 className="size-3" />
                  </button>
                )}
              </span>
            ) : null}
          </span>
        );
      })}

      {/* Only for an operator who may change the mailbox: the menu's entries
          are all writes or opens, and both need `manage:crm.email`. */}
      <ContextMenu menu={menu} label={t("email.imap.menu")}>
        {(f, close) => (
          <ImapFolderItems
            folder={f}
            close={close}
            openable={f.selectable}
            locked={isProtected(f)}
            hasChildren={parents.has(f.path)}
            onOpen={() => onOpen(f.path)}
            onMarkRead={() => void markRead(f)}
            onNewInside={() => setAdding({ parent: f.path })}
            onRename={() => setRenaming(f)}
            onDelete={() => setDeleting(f)}
            onRefresh={() => void load()}
            pin={onPin ? { pinned: pinned?.(f.path) ?? false, onToggle: () => onPin(f.path) } : undefined}
          />
        )}
      </ContextMenu>

      {adding ? (
        <NewImapFolderModal
          mailbox={mailbox}
          folders={folders}
          defaultParent={adding.parent}
          onClose={() => setAdding(null)}
          onCreated={(path) => {
            onToast(t("email.imap.created", { name: path }));
            void load();
          }}
        />
      ) : null}

      {renaming ? (
        <RenameImapFolderModal
          mailbox={mailbox}
          folder={renaming}
          onClose={() => setRenaming(null)}
          onRenamed={(path) => {
            onToast(t("email.imap.renamed", { name: path }));
            // The open folder follows its own rename rather than being closed:
            // the operator renamed what they were reading, and the mail in it
            // did not go anywhere. An ANCESTOR counts too - IMAP RENAME carries
            // the subtree, so renaming "Clients" moves the open "Clients/2026"
            // to "Kliendid/2026". The delimiter is part of the test, or a
            // sibling called "Clients2" would be dragged along with it.
            const moved = renaming.path;
            if (openPath === moved) onOpen(path);
            else if (openPath?.startsWith(moved + delimiter)) {
              onOpen(path + openPath.slice(moved.length));
            }
            void load();
          }}
        />
      ) : null}

      {deleting ? (
        <DeleteImapFolderModal
          mailbox={mailbox}
          folder={deleting}
          onClose={() => setDeleting(null)}
          onDeleted={(path) => {
            onToast(t("email.imap.deleted", { name: path }));
            // Reading a folder that no longer exists is not a state worth
            // keeping - the panes go back to the CRM's own inbox.
            if (path === openPath) onClose();
            void load();
          }}
        />
      ) : null}
    </>
  );
}
